import React, { useState } from 'react';
import { Box, TextField, Button, CircularProgress } from '@mui/material';
import { db } from '../../firebaseConfig';
import { collection, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { useUser } from '../../context/UserContext';
import { useSigilContext } from '../../context/sigilContext';

const CommentForm = ({ 
    parentId = null, 
    onCancel = null, 
    onSuccess = null, 
    initialText = '', 
    isEditing = false, 
    commentId = null 
}) => {
    const [text, setText] = useState(initialText);
    const [loading, setLoading] = useState(false);
    const { user } = useUser();
    const { i18n } = useSigilContext();
    const translate = i18n?.forum || {};

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!text.trim() || !user) return;

        setLoading(true);
        try {
            if (isEditing && commentId) {
                const commentRef = doc(db, 'comments', commentId);
                await updateDoc(commentRef, {
                    text: text.trim(),
                    updatedAt: serverTimestamp(),
                });
            } else {
                await addDoc(collection(db, 'comments'), {
                    text: text.trim(),
                    userId: user.id,
                    userName: user.name,
                    parentId: parentId,
                    likes: [],
                    dislikes: [],
                    createdAt: serverTimestamp(),
                });
            }

            if (!isEditing) setText('');
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error("Error submitting comment: ", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ marginTop: 2, marginBottom: 2 }}>
            <TextField
                fullWidth
                multiline
                minRows={isEditing ? 4 : (parentId ? 2 : 3)}
                variant="outlined"
                placeholder={parentId ? (translate.replyPlaceholder || "Escribe una respuesta...") : (translate.commentPlaceholder || "Deja un comentario...")}
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={loading}
                sx={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                            borderColor: 'white',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'white',
                        },
                        // Permite redimensionar manualmente
                        '& textarea': {
                            resize: 'vertical',
                        }
                    },
                    '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                    },
                }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1, gap: 1 }}>
                {onCancel && (
                    <Button onClick={onCancel} variant="text" color="inherit" disabled={loading}>
                        {translate.cancelButton || 'Cancelar'}
                    </Button>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    disabled={!text.trim() || loading || (isEditing && text.trim() === initialText)}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isEditing 
                        ? (translate.updateButton || 'Actualizar')
                        : (parentId ? (translate.replyButton || 'Responder') : (translate.publishButton || 'Publicar'))
                    }
                </Button>
            </Box>
        </Box>
    );
};

export default CommentForm;
